import { useState, type ReactElement } from 'react';
import { Check, ChevronDown, RotateCcw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { trackEvent } from '@/lib/analytics';
import {
  useOverrideAlertSeverity,
  useResetAlertSeverity,
  type AlertSeverityScope,
} from '@/hooks/api/useAlertSeverityOverride';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import {
  Severity,
  severityOptionsDescending,
} from '@kirin/api-client/contracts/shared/severity';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import { SEVERITY_OVERRIDE_REACH } from '@/components/alertSeverityCopy';

/** Props for {@link AlertSeverityOverrideCell}. */
export interface AlertSeverityOverrideCellProps {
  scope: AlertSeverityScope;
  alert: GroupedAlert;
  /**
   * Human label for the alert's detection rule, shown in the success toast
   * ("Severity updated to X for <label>"). Defaults to the alert type when the
   * caller has no richer title.
   */
  detectionRuleLabel?: string;
}

/**
 * The current effective severity of the alert (already the overridden value
 * when an override exists — the backend applies the COALESCE).
 */
function effectiveSeverity(alert: GroupedAlert): Severity {
  return alert.severity as Severity;
}

/**
 * The badge as shown in the cell: the effective severity, plus (when
 * overridden) a dot indicator and an "Originally X · Adjusted by Y" tooltip.
 * Used both for the read-only (no-permission) render and as the dropdown
 * trigger. `interactive` adds the affordance styles for the trigger case.
 */
function OverriddenSeverityBadge({
  scope,
  alert,
  interactive,
}: {
  scope: AlertSeverityScope;
  alert: GroupedAlert;
  interactive: boolean;
}): ReactElement {
  const override = alert.severityOverride;
  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        interactive && 'cursor-pointer'
      )}
    >
      <span className="relative inline-flex">
        <SeverityBadge riskLevel={effectiveSeverity(alert)} showIcon />
        {override?.overridden && (
          <span
            aria-label="Severity overridden"
            className="absolute -right-1 -top-1 size-2 rounded-full bg-amber-500 ring-2 ring-background"
          />
        )}
      </span>
      {interactive && (
        <ChevronDown
          className="size-4 text-muted-foreground"
          aria-hidden="true"
        />
      )}
    </span>
  );

  if (!override?.overridden) {
    return badge;
  }

  // KIR-2364: Singleton (personal scope) has no other user to attribute to, so
  // the tooltip reads "Adjusted by User" per the ticket; org scope names the
  // manager who made the change (falling back if the actor row was deleted).
  const adjustedBy =
    scope.kind === 'account'
      ? 'User'
      : (override.overriddenByName ??
        override.overriddenByEmail ??
        'a manager');

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>
        Originally {getSeverityLabel(override.originalSeverity)} · Adjusted by{' '}
        {adjustedBy}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Clickable severity cell for the Alerts table (KIR-2364).
 *
 * Owner/Manager (org scope) or the account owner (personal scope) get a
 * dropdown to override the alert group's severity, with a confirmation modal
 * before every change and a "Reset to Default" option once an override exists.
 * Everyone else — and everyone when the `alert_severity_override` flag is off —
 * sees the plain badge (with the override indicator/tooltip when present).
 */
export function AlertSeverityOverrideCell({
  scope,
  alert,
  detectionRuleLabel,
}: AlertSeverityOverrideCellProps): ReactElement {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_SEVERITY_OVERRIDE,
  ]);
  const featureEnabled =
    flags[flagsmithFeatureFlags.ALERT_SEVERITY_OVERRIDE] === true;

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  const { toast } = useToast();
  const overrideMutation = useOverrideAlertSeverity(scope);
  const resetMutation = useResetAlertSeverity(scope);

  // The severity awaiting confirmation, or 'reset' for reset-to-default. `null`
  // = modal closed.
  const [pending, setPending] = useState<Severity | 'reset' | null>(null);

  const current = effectiveSeverity(alert);
  const override = alert.severityOverride;
  const label = detectionRuleLabel ?? alert.type;

  const isBusy = overrideMutation.isPending || resetMutation.isPending;

  // Gate: flag on AND (personal scope, always allowed) OR (org scope with
  // owner/manager access). Support (disabled) and members (hidden) get no
  // dropdown — just the read-only badge.
  const canEdit =
    featureEnabled &&
    !flagsLoading &&
    (scope.kind === 'account' ||
      (!writeGate.isLoading && !writeGate.hidden && !writeGate.disabled));

  if (!canEdit) {
    return (
      <OverriddenSeverityBadge
        scope={scope}
        alert={alert}
        interactive={false}
      />
    );
  }

  const submitOverride = createMutationHandler(overrideMutation, toast, {
    successMessage: (result): string =>
      `Severity updated to ${getSeverityLabel(
        result.overriddenSeverity ?? current
      )} for ${label}`,
    errorMessage: 'Failed to update severity',
  });
  const submitReset = createMutationHandler(resetMutation, toast, {
    successMessage: (result): string =>
      `Severity reset to ${getSeverityLabel(result.originalSeverity)} for ${label}`,
    errorMessage: 'Failed to reset severity',
  });

  const confirm = async (): Promise<void> => {
    if (pending === null) {
      return;
    }
    try {
      if (pending === 'reset') {
        await submitReset({ alertKey: alert.alertKey });
        trackEvent('alert_severity_reset', {
          scope: scope.kind,
          alert_key: alert.alertKey,
        });
      } else {
        await submitOverride({ alertKey: alert.alertKey, severity: pending });
        trackEvent('alert_severity_overridden', {
          scope: scope.kind,
          alert_key: alert.alertKey,
          from_severity: current,
          to_severity: pending,
        });
      }
    } catch {
      // createMutationHandler already surfaced the failure toast.
    } finally {
      setPending(null);
    }
  };

  // The value the confirmation modal is moving TO — a chosen severity, or the
  // original severity for a reset.
  const targetSeverity =
    pending === 'reset' ? (override?.originalSeverity ?? current) : pending;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          disabled={isBusy}
          onClick={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
        >
          <button type="button" aria-label="Change severity">
            <OverriddenSeverityBadge scope={scope} alert={alert} interactive />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onClick={e => e.stopPropagation()}>
          {severityOptionsDescending.map(option => (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => {
                if (option.value !== current) {
                  setPending(option.value);
                }
              }}
            >
              <SeverityBadge riskLevel={option.value} showIcon />
              {option.value === current && (
                <Check className="ml-auto size-4" aria-label="Current" />
              )}
            </DropdownMenuItem>
          ))}
          {override?.overridden && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setPending('reset')}>
                <RotateCcw className="size-4" />
                Reset to Default: {getSeverityLabel(override.originalSeverity)}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={pending !== null}
        onOpenChange={open => {
          if (!open) {
            setPending(null);
          }
        }}
      >
        <DialogContent onClick={e => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>
              Change Severity from {getSeverityLabel(current)} to{' '}
              {targetSeverity ? getSeverityLabel(targetSeverity) : ''}?
            </DialogTitle>
            <DialogDescription>
              This action will update the severity assigned to this alert.{' '}
              {SEVERITY_OVERRIDE_REACH}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPending(null)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button onClick={() => void confirm()} disabled={isBusy}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
